import { CriteriaSet } from '../../../shared/criteria/domain/criteria';

export type CriteriaKey =
  | 'operationalManagement'
  | 'storagePersistance'
  | 'unlimitedExecutionTimeout'
  | 'containerizationLimitations'
  | 'asyncExecution'
  | 'scalability'
  | 'etlOriented'
  | 'vendorLockIn'
  | 'batchingProcessing';

export const COMPUTE_CRITERIA_SET: CriteriaSet<CriteriaKey> = {
  asyncExecution: {
    name: 'Async Execution',
    description:
      'Consider initiation factors to execute a task or process based on specific events, triggers or periodic-based time.',
    weight: 1,
    prompt:
      'How significant is for your application to be initiated on-demand, based on an event or in a period-time basis?',
    choices: [
      {
        title:
          '⭐️ [Not suitable]; My application must be only be initiated based in a direct request.',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; My application for its nature may need to be initiated only if a event happens or in a period-time basis',
        value: 10,
      },
    ],
  },
  scalability: {
    name: 'Scalability',
    description:
      'Evaluate the resources ability to handle high volumes of data and scale out according to the requests',
    weight: 1,
    prompt:
      'How important is it for your application can scale efficiently if is needed and with minimal effort?',
    choices: [
      {
        title:
          '⭐️ [Not a concern]; application performance under heavy loads is not critical and we can allocate efforts to do it without managed resources',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; performance and scalability are key requirements for optimal user experience and business growth',
        value: 10,
      },
    ],
  },
  unlimitedExecutionTimeout: {
    name: 'Unlimited Execution Timeout',
    description:
      'Assess the maximum duration allowed for a process, task, or operation to complete before it is terminated or considered unsuccessful.',
    weight: 1,
    prompt:
      'When it comes to your application, it is well segmented and needs less than 15 minutes for it to be executed successfully, considering delays for external resources?',
    choices: [
      {
        title:
          '⭐️ [Not a concern]; My application is well segmented and for it to finish sucessfully needs less than 15 minutes.',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; My application for its nature may needs more than 15 minutes to finish all its process.',
        value: 10,
      },
    ],
  },
  storagePersistance: {
    name: 'Storage Persistance',
    description:
      'Provide direct storage options to store non-volatile information, such as a hard drive, that can be retrieved even after a reboot/shutdown or failulre, but no external storage cannot be used.',
    weight: 1,
    prompt:
      'How important is storage persistence for your system inside the same boundaries where is executed?',
    choices: [
      {
        title:
          '⭐️ [Not Important]; My data, can be stored in an external storage or resource as Database, Distribuited File system or Object Storage',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Top priority]; My data needs to be stored and retrieved inside the same execution runtime where the system is being executed',
        value: 10,
      },
    ],
  },
  vendorLockIn: {
    name: 'Vendor Lock-In',
    description:
      'Evaluate the External Application requirements for specific and non-changable specifications provided by the Third-Party.',
    weight: 1,
    prompt:
      'Is your application provided by a third party and it has specific and non-changable requirements that is not possible to use Cloud Managed Services?',
    choices: [
      {
        title:
          '⭐️ [Not a concern]: The application is in-house or can be deployed in an Cloud Managed Service',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; The application is has specific requirements and it is not suitable for Cloud Managed Services',
        value: 10,
      },
    ],
  },
  containerizationLimitations: {
    name: 'Containerization Limitations',
    description:
      'Approach that enables the packaging and isolation of applications and their dependencies into self-contained units called containers. It involves encapsulating an application and all its necessary runtime components, libraries, and configurations in a standardized format that can be easily deployed and executed across different computing environments.',
    weight: 1,
    prompt:
      'Regarding to your application, is there a limitation to be packaged into a docker container?',
    choices: [
      {
        title:
          '⭐️ [Not a concern]; My application is suitable to be packaged into a docker container',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; My application for its nature cannot be packaged inside a docker container',
        value: 10,
      },
    ],
  },
  operationalManagement: {
    name: 'Operational management',
    description:
      'Assess the level of effort, complexity, and expertise required to effectively operate and manage a particular resource.',
    prompt:
      'How important is to manage every single aspect for your application stack, involving configuration, patching, scaling and provisioning?',
    weight: 1,
    choices: [
      {
        title: '⭐️ [Not important]; I prefer minimal effort required',
        value: 2,
      },
      {
        title:
          '⭐️⭐️⭐️ [Some importance]; Prefer a moderate effort for defining and configuring some aspects',
        value: 6,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [A top priority]; Is vital to be able to manage every single aspect for configuration, patching and scaling.',
        value: 10,
      },
    ],
  },
  etlOriented: {
    name: 'ETL Oriented',
    description:
      'Evaluatue the resources ability to handle Extract, Transform and Load Oriented operations.',
    weight: 1,
    prompt: 'How important is for your process to handle ETL Operations?',
    choices: [
      {
        title:
          '⭐️ [Not a concern]: My process does not need to execute ETL opertions',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; My process are mainly to execute ETL Operations',
        value: 10,
      },
    ],
  },
  batchingProcessing: {
    name: 'Batching Processing',
    description:
      'Asses the Application process for Batching Operations to be done.',
    weight: 1,
    prompt:
      'How important for your process to be able to process batching tasks?',
    choices: [
      {
        title:
          '⭐️ [Not a concern]: The application does not need to be able to do Batching Processing',
        value: 1,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; The application is specially developed to be able to do Batching Processing',
        value: 10,
      },
    ],
  },
};
